// react imports
import { useState, useEffect } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { List, Divider, Link, Icon, CircularProgress } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
// import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setUserStudies,
  setActiveNavLink,
  setCurrentStudy,
} from "context";

// Firebase imports
import { getDocs, collection } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db, logout } from "../../Firebase";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  // add to below: sidenavColor
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    userStudies,
    activeNavLink,
    currentStudy,
  } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  const [studiesNotLoaded, setStudiesNotLoaded] = useState(true);

  let textColor = "white";

  const navLinks = ["dashboard", "tracking", "inventory", "directory"];

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  useEffect(() => {
    const getStoreDocs = async () => {
      const auth = getAuth();
      const userInfo = auth.currentUser;
      const storeUser = userInfo.uid || userInfo.email;
      const colRef = await getDocs(collection(db, "USERS", storeUser, "STUDIES"));
      const userStudyDocs = colRef.docs.map((el) => el.id);
      setUserStudies(dispatch, userStudyDocs);
      setStudiesNotLoaded(false);
    };
    getStoreDocs();
  }, []);

  useEffect(() => {
    if (dispatch) {
      setCurrentStudy(dispatch, userStudies[0]);
      setActiveNavLink(dispatch, userStudies[0]);
      navLinks.map((navLink) => {
        if (window.location.href.indexOf(`/${navLink}`) > -1) {
          setActiveNavLink(dispatch, navLink);
        }
        return true;
      });
      console.log(activeNavLink);
      console.log(currentStudy);
      console.log(userStudies);
    }
  }, [userStudies, dispatch]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes
    .filter((c) => c.key !== "sign-out")
    .map(({ type, name, icon, title, noCollapse, key, href, route }) => {
      let returnValue;

      if (type === "collapse") {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    });

  const renderUserStudies = userStudies.map((el) => (
    <NavLink key={el} to="/">
      <SidenavCollapse
        name={el.replace(/_/g, " ")}
        icon="arrow_forward"
        active={activeNavLink === el}
        color="white"
        onClick={() => {
          setActiveNavLink(dispatch, el);
          setCurrentStudy(dispatch, el);
        }}
      />
    </NavLink>
  ));

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="1.5rem" mx={1} />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      {/* <List>
        <NavLink key="dashboard" to="/dashboard">
          <SidenavCollapse
            name="Dashboard"
            active={activeNavLink === "dashboard"}
            icon={<Icon fontSize="small">dashboard</Icon>}
            onClick={() => {
              setActiveNavLink(dispatch, "dashboard");
            }}
          />
        </NavLink>
      </List> */}
      <List>
        {studiesNotLoaded && (
          <MDBox px={2}>
            <CircularProgress color="white" />
          </MDBox>
        )}
        {renderUserStudies}
      </List>
      <List>{renderRoutes}</List>
      <List>
        <NavLink key="tracking" to="/tracking">
          <SidenavCollapse
            name="Bulk Tracking"
            active={activeNavLink === "tracking"}
            icon={<Icon fontSize="small">share-location</Icon>}
            onClick={() => {
              setActiveNavLink(dispatch, "tracking");
            }}
          />
        </NavLink>
        {/* <NavLink key="inventory" to="/inventory">
          <SidenavCollapse
            name="Inventory"
            active={activeNavLink === "inventory"}
            icon={<Icon fontSize="small">inventory</Icon>}
            onClick={() => {
              setActiveNavLink(dispatch, "inventory");
            }}
          />
        </NavLink> */}
        <NavLink key="directory" to="/directory">
          <SidenavCollapse
            name="User Directory"
            active={activeNavLink === "directory"}
            icon={<Icon fontSize="small">person</Icon>}
            onClick={() => {
              setActiveNavLink(dispatch, "directory");
            }}
          />
        </NavLink>
        <Divider light />
        <NavLink key="sign-out" to="/authentication/sign-in">
          <SidenavCollapse
            name="Sign Out"
            icon={<Icon fontSize="small">login</Icon>}
            onClick={() => {
              setActiveNavLink(dispatch, "sign-in");
              logout();
            }}
          />
        </NavLink>
      </List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
